import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDeliveriesApi, changeDriverApi, massiveChangeStatusApi, fulfillDeliveriesApi, getSelectedDeliveriesApi, createSingleOrderApi, uploadOrdersExcelApi, exportDeliveriesApi, exportStatusHistoryApi, getDeliveryFilterOptionsApi, dispatchDeliveriesApi, changeDeliveriesCourierApi, changeDeliveriesPickupPointApi, getCourierOptionsApi } from './deliveriesApi';
import { getMode } from '../../utils/functions';
import { extractLabelUrls } from './deliveriesUtils';
import { mergeLabelPdfs } from '../../services/pdfService';
import { extractApiError, isApiUnreachable } from '../../utils/errors';
import { notificationStarted } from '../notifications/notificationsSlice';
import { NOTIFICATION_KIND } from '../notifications/notificationsConstants';
import { progressCleared, progressUpdated } from '../feedback/feedbackSlice';
import { uploadProgressReporter } from '../feedback/uploadProgress';
import {
  orderUploadCleared,
  orderUploadQueued,
  orderUploadSending,
  orderUploadSendProgressed,
} from './orderUploadSlice';

/**
 * The listing's query for a set of filters. `admin_id` is the one filter the
 * backend reads as an id rather than as a word: sent as the `'all'` sentinel
 * it would end up in `sellers_seller.admin_id = 'all'`. The rest of the
 * sentinels the query does understand.
 */
const listingQuery = (filters) => {
  const { admin_id: adminId, ...rest } = filters || {};
  return adminId && adminId !== 'all' ? { ...rest, admin_id: adminId } : rest;
};

/**
 * How many of the requested orders a bulk endpoint changed. They answer with a
 * sentence and no numbers — "12 / 15 Envios actualizados", "12 Envíos
 * actualizados..." — so the count is read out of it. Without one, everything
 * requested is assumed changed.
 *
 * @param {unknown} message
 * @param {number} requested
 * @returns {{requested: number, updated: number}}
 */
const readCounts = (message, requested) => {
  const text = typeof message === 'string' ? message : '';
  const counted = text.match(/^\s*(\d+)/);
  const updated = counted ? Number(counted[1]) : requested;
  return { requested, updated: Math.min(updated, requested) };
};

/**
 * A change that got no answer may have been applied. The api interceptor
 * already says so; marking the rejection keeps the action from adding a
 * "failed" of its own that would contradict it.
 */
const unconfirmedMeta = (error) => ({ unconfirmed: isApiUnreachable(error) });

export const getDeliveries = createAsyncThunk(
  'deliveries/getDeliveries',
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetchDeliveriesApi(listingQuery(params));
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los envíos'));
    }
  }
);

/**
 * The admin's companies, for the `company` filter.
 *
 * Fetched once per session: it is a catalog, and the same list feeds both the
 * deliveries table and the dashboard. The `company` filter has to carry an id —
 * the backend compares it against `companies_company.id` — which is why the
 * hardcoded social reasons it used to offer could never have worked.
 */
export const fetchDeliveryFilterOptions = createAsyncThunk(
  'deliveries/fetchDeliveryFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDeliveryFilterOptionsApi();
      return response.data;
    } catch (error) {
      console.error('Error fetching delivery filter options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las empresas'));
    }
  },
  { condition: (_, { getState }) => !getState().deliveries.companyOptionsLoaded }
);

/**
 * Hands a batch of shipments (by ORDER id) to one driver. Resolves with how many
 * changed and the driver's name, for the toast that reports it.
 */
export const changeDriver = createAsyncThunk(
  'deliveries/changeDriver',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const response = await changeDriverApi(payload);
      const driver = (getState().orderDetails?.driverOptions || []).find(
        (option) => String(option.value) === String(payload.driver_id)
      );
      return {
        ...readCounts(response.data?.message, payload.ids.length),
        driverName: driver?.label ?? null,
      };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo asignar el conductor'), unconfirmedMeta(error));
    }
  }
);

/**
 * Sets one status on a batch of shipments (by ORDER id). Resolves with how many
 * changed: the backend leaves the finished ones alone without saying which.
 */
export const massiveChangeStatus = createAsyncThunk(
  'deliveries/massiveChangeStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await massiveChangeStatusApi(payload);
      return { ...readCounts(response.data?.message, payload.ids.length), status: payload.status };
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo cambiar el estado de los envíos'), unconfirmedMeta(error));
    }
  }
);

/**
 * Why "prepare" did nothing, which the backend's 400 does not say. The status
 * is not the only filter: shipments ceded by a partner are not prepared either.
 */
const NOTHING_TO_PREPARE =
  'Ninguno de los envíos seleccionados se pudo preparar: solo se preparan los de tu operación (no los cedidos por un partner) que están Pendiente, Creado en courier, Retirado o Preparado.';

/**
 * Marks a batch of shipments (by ORDER id) as prepared. Resolves with how many
 * changed: the backend skips every status it does not prepare without a word.
 */
export const fulfillDeliveries = createAsyncThunk(
  'deliveries/fulfillDeliveries',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await fulfillDeliveriesApi(payload);
      return readCounts(response.data?.message, payload.ids.length);
    } catch (error) {
      if (error?.response?.status === 400 && /no actualizados/i.test(error.response.data?.message || '')) {
        return rejectWithValue(NOTHING_TO_PREPARE);
      }
      return rejectWithValue(extractApiError(error, 'No se pudieron preparar los envíos'), unconfirmedMeta(error));
    }
  }
);

/** The backend's cap on ids per request (`BULK_DISPATCH_MAX_IDS`). */
const DISPATCH_BATCH_SIZE = 100;

/**
 * Sends a selection in batches the backend takes, one after the other, and
 * merges their answers: `{requested, queued, skipped, failed}`.
 *
 * A batch the backend refuses does not stop the rest — its ids land in `failed`
 * with the reason. A batch with no answer does stop it: the server may have
 * queued it anyway, so it and the ones after it are reported as unconfirmed
 * rather than sent again into a dead API. With more than one batch it reports
 * which one is going out, under `requestId` in the feedback slice.
 *
 * @returns {Promise<{result: object, answered: boolean, firstError: string|null}>}
 */
const sendInBatches = async (ids, send, { dispatch, requestId, fallbackMessage, succeededKey = 'queued' }) => {
  const result = { requested: 0, queued: [], skipped: [], failed: [] };
  const batches = Math.ceil(ids.length / DISPATCH_BATCH_SIZE);
  let answered = false;
  let firstError = null;

  for (let start = 0; start < ids.length; start += DISPATCH_BATCH_SIZE) {
    const batch = ids.slice(start, start + DISPATCH_BATCH_SIZE);
    const batchNumber = start / DISPATCH_BATCH_SIZE + 1;

    if (batches > 1) {
      dispatch(progressUpdated({
        key: requestId,
        done: batchNumber - 1,
        total: batches,
        label: `Lote ${batchNumber} de ${batches}`,
      }));
    }

    try {
      const { data } = await send(batch);
      answered = true;
      result.requested += data?.requested ?? batch.length;
      result.queued.push(...(data?.[succeededKey] || []));
      result.skipped.push(...(data?.skipped || []));
    } catch (error) {
      if (isApiUnreachable(error)) {
        const unconfirmed = ids.slice(start);
        const message =
          'No hubo respuesta del servidor: revisa el historial antes de reintentar, algunos pueden haberse enviado.';
        firstError = firstError ?? message;
        result.requested += unconfirmed.length;
        result.failed.push(...unconfirmed.map((deliveryId) => ({ delivery_id: deliveryId, message })));
        break;
      }

      const message = extractApiError(error, fallbackMessage);
      firstError = firstError ?? message;
      result.requested += batch.length;
      result.failed.push(...batch.map((deliveryId) => ({ delivery_id: deliveryId, message })));
    }
  }

  dispatch(progressCleared(requestId));
  return { result, answered, firstError };
};

/**
 * Sends the selected shipments to their courier, skipping the preparation
 * rules.
 *
 * A selection survives paging, so it can outgrow the endpoint's cap: it goes
 * out in sequential batches whose answers are merged. A batch the backend
 * refuses does not stop the rest — its ids land in `failed` with the reason —
 * and the thunk only rejects when no batch got through. A batch with no answer
 * does stop it: the server may have queued it anyway, so it and the ones after
 * it are reported as unconfirmed rather than sent again into a dead API.
 *
 * Kept out of the slice on purpose: `isLoading` drives the table's spinner.
 * With more than one batch it reports which one is going out, under its
 * `requestId` in the feedback slice.
 *
 * @param {number[]} deliveryIds
 * @returns {{requested: number, queued: Array<object>, skipped: Array<object>,
 *   failed: Array<{delivery_id: number, message: string}>}}
 */
export const dispatchDeliveriesToCourier = createAsyncThunk(
  'deliveries/dispatchDeliveriesToCourier',
  async (deliveryIds, { dispatch, requestId, rejectWithValue }) => {
    const { result, answered, firstError } = await sendInBatches(deliveryIds, dispatchDeliveriesApi, {
      dispatch,
      requestId,
      fallbackMessage: 'No se pudieron enviar los envíos al courier',
    });
    if (firstError && !answered) return rejectWithValue(firstError);
    return result;
  }
);

/**
 * Hands the selected shipments to another courier, with the rules of the
 * order detail's «Cambiar courier». In batches, like sending to the courier:
 * see `sendInBatches`. Kept out of the slice for the same reason.
 *
 * @param {{deliveryIds: number[], courier: string}} arg
 * @returns {{requested: number, courier: string, queued: Array<object>, skipped: Array<object>,
 *   failed: Array<{delivery_id: number, message: string}>}}
 */
export const changeDeliveriesCourier = createAsyncThunk(
  'deliveries/changeDeliveriesCourier',
  async ({ deliveryIds, courier }, { dispatch, requestId, rejectWithValue }) => {
    const { result, answered, firstError } = await sendInBatches(
      deliveryIds,
      (batch) => changeDeliveriesCourierApi(batch, courier),
      { dispatch, requestId, fallbackMessage: 'No se pudo cambiar el courier de los envíos' }
    );
    if (firstError && !answered) return rejectWithValue(firstError);
    return { ...result, courier };
  }
);

/**
 * Sends the selected shipments to a pickup point, with the rules of the order
 * detail's «Entregar en punto de retiro». In batches, like changing the
 * courier: see `sendInBatches`. The backend answers `redirected` where the
 * other bulk actions answer `queued`, because nothing waits in a worker.
 *
 * @param {{deliveryIds: number[], pickupPointId: number}} arg
 * @returns {{requested: number, pickupPointId: number, redirected: Array<object>,
 *   skipped: Array<object>, failed: Array<{delivery_id: number, message: string}>}}
 */
export const changeDeliveriesPickupPoint = createAsyncThunk(
  'deliveries/changeDeliveriesPickupPoint',
  async ({ deliveryIds, pickupPointId }, { dispatch, requestId, rejectWithValue }) => {
    const { result, answered, firstError } = await sendInBatches(
      deliveryIds,
      (batch) => changeDeliveriesPickupPointApi(batch, pickupPointId),
      {
        dispatch,
        requestId,
        fallbackMessage: 'No se pudo cambiar el destino de los envíos',
        succeededKey: 'redirected',
      }
    );
    if (firstError && !answered) return rejectWithValue(firstError);
    const { queued, ...rest } = result;
    return { ...rest, redirected: queued, pickupPointId };
  }
);

/**
 * The couriers the bulk «Cambiar courier» offers: the operator's own, or, for
 * the staff, those of the operator it filtered by. Kept per operator, and asked
 * once each.
 *
 * @param {number|string|null} [adminId]
 */
export const fetchCourierOptions = createAsyncThunk(
  'deliveries/fetchCourierOptions',
  async (adminId, { rejectWithValue }) => {
    try {
      const { data } = await getCourierOptionsApi(adminId);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudieron cargar los couriers'));
    }
  },
  {
    condition: (adminId, { getState }) => !getState().deliveries.courierOptions?.[courierOptionsKey(adminId)],
  }
);

/** Where the couriers of an operator are kept: by its id, or `own` for the session's. */
export const courierOptionsKey = (adminId) => (adminId ? String(adminId) : 'own');

/**
 * Merges the labels of these items into one PDF and opens it, reporting what it
 * covered: how many bultos had a label, which labels could not be read, and
 * whether the browser let the tab open. Progress goes to the feedback slice
 * under the thunk's `requestId`, which is the id of its progress toast.
 */
const buildLabelsPdf = async (items, { dispatch, requestId }) => {
  const urls = extractLabelUrls(items);
  const report = (done) => dispatch(progressUpdated({ key: requestId, done, total: urls.length }));

  report(0);
  const { blob, failed } = await mergeLabelPdfs(urls, { onProgress: report });
  const url = URL.createObjectURL(blob);

  // Opened here, after the downloads, so a popup blocker may stop it. The
  // outcome toast then carries a button that opens it inside a click.
  const opened = Boolean(window.open(url, '_blank'));

  return {
    url,
    opened,
    bultos: items.length,
    included: urls.length - failed.length,
    withoutLabel: items.length - urls.length,
    unreadable: failed.map((label) => label.name),
  };
};

/** One label download at a time: the menu closes on click and invites a second. */
const noLabelsRunning = (_, { getState }) => !getState().deliveries.isDownloadingLabels;

/**
 * The labels of the page of the history on screen, in one PDF.
 *
 * The same query as the table, page included: the endpoint always pages, so
 * this was never "every label", and it used to leave out the operator, driver
 * and cession filters, printing labels of rows the table did not show. The
 * dates go straight through: the store already holds `YYYY-MM-DD`.
 */
export const downloadAllLabels = createAsyncThunk(
  'deliveries/downloadAllLabels',
  async ({ filters, user }, { dispatch, requestId, rejectWithValue }) => {
    try {
      const response = await fetchDeliveriesApi({ ...listingQuery(filters), mode: getMode(user) });
      const items = response.data?.delivery_items_collection || [];
      if (extractLabelUrls(items).length === 0) throw new Error('No hay etiquetas en esta página.');
      return await buildLabelsPdf(items, { dispatch, requestId });
    } catch (error) {
      return rejectWithValue(extractApiError(error, error.message || 'No se pudieron descargar las etiquetas'));
    } finally {
      dispatch(progressCleared(requestId));
    }
  },
  { condition: noLabelsRunning }
);

/** The labels of the selected shipments (by ORDER id), in one PDF. */
export const downloadSelectedLabels = createAsyncThunk(
  'deliveries/downloadSelectedLabels',
  async ({ ids, user }, { dispatch, requestId, rejectWithValue }) => {
    try {
      const response = await getSelectedDeliveriesApi({ mode: getMode(user), selectedLabels: ids });
      const items = response.data?.delivery_items_collection || [];
      if (extractLabelUrls(items).length === 0) {
        throw new Error('Ninguno de los envíos seleccionados tiene etiqueta todavía.');
      }
      return await buildLabelsPdf(items, { dispatch, requestId });
    } catch (error) {
      return rejectWithValue(extractApiError(error, error.message || 'No se pudieron descargar las etiquetas seleccionadas'));
    } finally {
      dispatch(progressCleared(requestId));
    }
  },
  { condition: noLabelsRunning }
);

/**
 * Creates one order from the manual form.
 *
 * Takes a {@link import('./deliveriesApi').SingleOrderPayload}, already shaped
 * by the form: the shipping address differs per country, so the screen builds
 * it and this thunk stays agnostic.
 */
export const createSingleOrder = createAsyncThunk(
  'deliveries/createSingleOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSingleOrderApi(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo crear la orden'));
    }
  }
);

/**
 * Hands a spreadsheet of orders to the backend and stops waiting.
 *
 * The POST only stores the file: the orders are created by a worker, row by
 * row, and the progress and the final summary arrive on the `order_upload`
 * socket channel. What comes back from here is just the acknowledgement.
 *
 * Takes `{ file, sellerId, warehouseId, country }`. `sellerId` is the admin's
 * pick; a seller leaves it out, because the backend takes the seller from the
 * session — and appending `undefined` would send the literal string.
 */
export const uploadOrdersExcel = createAsyncThunk(
  'deliveries/uploadOrdersExcel',
  async ({ file, sellerId, warehouseId, country }, { dispatch, rejectWithValue, requestId }) => {
    const formData = new FormData();
    formData.append('excel_file', file);
    if (sellerId) formData.append('seller_id', sellerId);
    formData.append('warehouse_id', warehouseId);
    formData.append('country', country);

    // The panel row opens on the click, like the exports do, so the user sees
    // the job exist before the worker says anything.
    dispatch(notificationStarted({ kind: NOTIFICATION_KIND.ORDER_UPLOAD, requestId }));
    dispatch(orderUploadSending());

    // A big file takes a while to go up, before the worker can say anything:
    // the bar at the top and the panel both show how much of it is sent.
    const report = uploadProgressReporter(dispatch, requestId, 'Subiendo el archivo')();
    const onUploadProgress = (event) => {
      report(event);
      dispatch(orderUploadSendProgressed({ sent: event.loaded, size: event.total }));
    };

    try {
      const response = await uploadOrdersExcelApi(formData, onUploadProgress);
      dispatch(orderUploadQueued());
      return response.data;
    } catch (error) {
      dispatch(orderUploadCleared());
      return rejectWithValue(extractApiError(error, 'No se pudo cargar el archivo'), {
        unconfirmed: isApiUnreachable(error),
      });
    }
  }
);

/**
 * Asks the backend for the deliveries spreadsheet over the current filter.
 *
 * The POST only queues the job — the result comes back minutes later on the
 * `download` socket channel, and ends up in the Centro de Actividad as a link
 * (the same file also goes out by email). The optimistic notification is
 * dispatched first so the panel and the button react to the click rather than
 * to the round trip.
 */
export const exportDeliveries = createAsyncThunk(
  'deliveries/exportDeliveries',
  async (_, { getState, dispatch, rejectWithValue, requestId }) => {
    const {
      start_date, end_date, company, search, delivery_status,
      advanced_filter_field: advancedField, advanced_filter_values: advancedValues,
    } = getState().deliveries.filters;

    dispatch(notificationStarted({ kind: NOTIFICATION_KIND.DOWNLOAD_DELIVERIES, requestId }));

    try {
      // The dates go straight through: `DeliveryFilters` normalises both the
      // string and the Date the picker can hand it, so the store already holds
      // the `YYYY-MM-DD` the backend wants. Running them through
      // `dateToDatabase` here would throw — it calls `toISOString()`, which a
      // string does not have.
      const response = await exportDeliveriesApi({
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        company,
        search,
        delivery_status,
        // Only when set: the backend refuses the keys to a plan without the
        // addon, and a plan that has it should export what the table shows.
        ...(advancedField && advancedValues
          ? { advanced_filter_field: advancedField, advanced_filter_values: advancedValues }
          : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo solicitar el reporte de envíos'), {
        unconfirmed: isApiUnreachable(error),
      });
    }
  }
);

/**
 * Same shape as {@link exportDeliveries}, for the status-history report.
 *
 * Staff only: the endpoint is behind `IsStaffAdminUser`, so the menu item that
 * calls this is hidden for everyone else rather than left to fail with a 403.
 */
export const exportStatusHistory = createAsyncThunk(
  'deliveries/exportStatusHistory',
  async (_, { getState, dispatch, rejectWithValue, requestId }) => {
    const { start_date, end_date, delivery_status } = getState().deliveries.filters;

    dispatch(notificationStarted({ kind: NOTIFICATION_KIND.DOWNLOAD_HISTORY, requestId }));

    try {
      const response = await exportStatusHistoryApi({
        start_date: start_date || undefined,
        end_date: end_date || undefined,
        delivery_status,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo solicitar el historial de estados'), {
        unconfirmed: isApiUnreachable(error),
      });
    }
  }
);
